import { Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SettingsService } from '@core';
import { ClientService } from '@core/services/client/client.service';
import { LookupCriteria, UserErrorMessage, UserModel, UserStatus } from '@core/services/user/user.model';
import { UserService } from '@core/services/user/user.service';
import { process } from '@progress/kendo-data-query';
import { GridSettings } from '@shared/components/customized-grid/grid-settings/gird-settings.interface';
import { NgxSpinnerService } from 'ngx-spinner';
import { BaseComponent } from '../base/base.component';
import { DescriptionModel } from '@core/services/base.model';

@Component({
  selector: 'Teranet-User',
  templateUrl: './user.component.html',
})
export class UserComponent extends BaseComponent {
  allClients: DescriptionModel[] = [];
  lookupCriteria: LookupCriteria;
  public gridSettings: GridSettings;
  public users: UserModel[] = [];
  userErrorMessage = UserErrorMessage;
  userForm: FormGroup;
  userStatus = UserStatus;

  statusKeys(): Array<string> {
    return Object.values(this.userStatus);
  }

  constructor(
    private clientService: ClientService,
    private router: Router,
    private settingsService: SettingsService,
    private spinner: NgxSpinnerService,
    private userService: UserService,
    public dialog: MatDialog
  ) {
    super(settingsService);
    this.userForm = new FormGroup({
      corporation: new FormControl(''),
      email: new FormControl(''),
      id: new FormControl(0),
      lastName: new FormControl(''),
      status: new FormControl(''),
      userName: new FormControl(''),
    });
  }

  public state: any = {
    skip: 0,
    take: 13,
    filter: {
      logic: 'and',
      filters: [],
    },
  };

  ngOnInit() {
    this.gridSettings = this.gridDefaultSettings();
    this.clientService.getClientInfoDescriptions().subscribe(data => {
      this.allClients = data as DescriptionModel[];
      // Default the Corporation Selection
      if (this.allClients.length == 1 && this.allClients[0].id != undefined) {
        this.userForm.get('corporation').setValue(this.allClients[0].id);
      }
    });
  }

  checkError() {
    if (this.userForm.get('corporation') !== null) {
      if (
        this.userForm.get('corporation').value === null ||
        typeof this.userForm.get('corporation').value !== 'number'
      ) {
        this.userForm.get('corporation').markAsTouched();
      }
    }
  }

  copyUser(id: number) {
    this.router.navigate(['edituser'], { queryParams: { copy: id } });
  }

  createUser() {
    this.router.navigateByUrl('/edituser');
  }

  lookup() {
    this.checkError();
    if (this.userForm.valid) {
      this.lookupCriteria = {
        clientInfoId: this.userForm.get('corporation').value,
        lastName: this.userForm.get('lastName').value ? this.userForm.get('lastName').value : '',
        userName: this.userForm.get('userName').value ? this.userForm.get('userName').value : '',
        email: this.userForm.get('email').value ? this.userForm.get('email').value : '',
        status: this.userForm.get('status').value ? this.userForm.get('status').value : '',
      };
      this.users = [];
      this.refresh();
    }
  }

  onClientChange() {
    this.users = [];
  }

  onStatusChange() {
    this.users = [];
  }

  refresh() {
    this.spinner.show();
    this.userService.lookupUsers(this.lookupCriteria).subscribe(result => {
      if (result) {
        this.users = result;
        this.gridSettings.gridData = { data: this.users, total: this.users.length };
      }
      this.spinner.hide();
    });
  }

  reset() {
    this.userForm.reset({
      status: '',
    });
    this.users = [];
  }

  gridDefaultSettings(): GridSettings {
    return {
      state: this.state,
      gridData: process(this.users, this.state),
      columnsConfig: [
        {
          field: 'id',
          filterable: false,
          hidden: false,
          includeInChooser: false,
          excludeFromExcel: true,
          title: 'Select',
          type: 'radio',
          width: 30,
        },
        {
          field: 'userName',
          filterable: true,
          hidden: false,
          includeInChooser: false,
          routerLink: '/edituser',
          title: 'User Name',
          width: 170,
        },
        {
          field: 'name',
          filterable: true,
          hidden: false,
          title: 'Name',
          width: 100,
        },
        {
          field: 'status',
          filterable: true,
          hidden: false,
          title: 'User Status',
          width: 100,
        },
        {
          field: 'roleDisplayName',
          filterable: true,
          hidden: false,
          title: 'Role',
          width: 100,
        },
        {
          field: 'email',
          filterable: true,
          hidden: false,
          title: 'Email',
          width: 170,
        },
        {
          field: 'lastLoginDateUtc',
          filter: 'date',
          filterable: true,
          format: 'yyyy-MM-dd HH:mm:ss',
          hidden: false,
          title: 'Last Login Date',
          type: 'dateUTC',
          width: 120,
        },
      ],
    };
  }
}
